import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useTranslations } from "@/hooks/useTranslations";
import { Loader2, Image as ImageIcon, Plus, X } from "lucide-react";
import { processImage } from "@/components/workouts/utils/imageProcessor";
import { toast } from "@/components/ui/use-toast";
import { LoadingButton } from "@/components/ui/loading-button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { CustomFileInput } from "@/components/ui/custom-file-input";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

interface CustomExerciseDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const getImagePreviewUrl = (file: File | null) => file ? URL.createObjectURL(file) : "";

export function CustomExerciseDialog({ open, onClose, onCreated }: CustomExerciseDialogProps) {
  const { t } = useTranslations("dashboard-exercise-library");
  const { user } = useAuth();

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const formSchema = z.object({
    name: z.string().min(1, t("custom.requiredField") || "Campo obrigatório"),
    body_part: z.string().min(1, t("custom.requiredField") || "Campo obrigatório"),
    equipment: z.string().optional(),
    target_muscle: z.string().optional(),
    difficulty: z.string().optional(),
    instruction_1: z.string().optional(),
    instruction_2: z.string().optional(),
    instruction_3: z.string().optional(),
    instruction_4: z.string().optional(),
    instruction_5: z.string().optional(),
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      body_part: "",
      equipment: "",
      target_muscle: "",
      difficulty: "",
      instruction_1: "",
      instruction_2: "",
      instruction_3: "",
      instruction_4: "",
      instruction_5: ""
    }
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const originalFile = e.target.files[0];
      try {
        const loadingToast = toast({
          title: t("custom.processingImage") || "Processando imagem...",
          description: t("custom.pleaseWait") || "Por favor, aguarde...",
        });
        
        const processedFile = await processImage(originalFile);
        setImageFile(processedFile);
        setImagePreviewUrl(getImagePreviewUrl(processedFile));
        
        toast({
          title: t("custom.imageReady") || "Imagem pronta",
          description: t("custom.imageProcessed") || "Imagem processada com sucesso",
          variant: "default",
        });
      } catch (err) {
        setImageFile(null);
        setImagePreviewUrl(null);
        
        toast({
          title: t("custom.imageError") || "Erro na imagem",
          description: t("custom.imageProcessingFailed") || "Falha ao processar a imagem",
          variant: "destructive",
        });
      }
    }
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile || !user?.id) return null;
    
    try {
      const fileExt = imageFile.name.split('.').pop();
      const filePath = `${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from("exercise-images")
        .upload(filePath, imageFile);
        
      if (error) {
        console.error("Upload error:", error);
        return null;
      }
      
      return filePath;
    } catch (err) {
      console.error("Exception during upload:", err);
      return null;
    }
  };

  const handleSubmit = async (data: FormValues) => {
    if (!user) {
      toast({
        title: t("custom.authError") || "Erro de autenticação",
        description: t("custom.notLoggedIn") || "Você precisa estar logado",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      let uploadedPath = null;
      if (imageFile) {
        uploadedPath = await uploadImage();
        if (!uploadedPath) {
          toast({
            title: t("custom.uploadFailed") || "Falha no upload",
            description: t("custom.imageUploadError") || "Não foi possível fazer upload da imagem",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      }

      const { instruction_1, instruction_2, instruction_3, instruction_4, instruction_5, ...exerciseFields } = data;
      const instructions = [instruction_1, instruction_2, instruction_3, instruction_4, instruction_5].filter(Boolean);

      const exerciseData = {
        ...exerciseFields,
        name: data.name,
        body_part: data.body_part,
        trainer_id: user.id,
        image_url: uploadedPath,
        instructions
      };

      const { error } = await supabase.from("custom_exercises").insert(exerciseData);

      if (error) {
        console.error("Database error:", error);
        toast({
          title: t("custom.createError") || "Erro ao criar",
          description: error.message || t("custom.databaseError") || "Erro no banco de dados",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      toast({
        title: t("custom.success") || "Sucesso!",
        description: t("custom.exerciseCreated") || "Exercício personalizado criado com sucesso",
      });
      
      onCreated();
      onClose();
      
      form.reset();
      setImageFile(null);
      setImagePreviewUrl(null);
    } catch (err) {
      console.error("Exception during submission:", err);
      toast({
        title: t("custom.createError") || "Erro ao criar",
        description: t("custom.unexpectedError") || "Ocorreu um erro inesperado",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] w-[95%] overflow-hidden p-0 bg-white/90 backdrop-blur-lg dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
            {t("custom.createTitle")}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[70vh] px-8 pb-6">
          <Form {...form}>
            <form className="space-y-5" onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="space-y-2">
                <FormLabel className="text-sm font-medium flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <ImageIcon className="h-4 w-4 text-flame" />
                  {t("custom.image")}
                </FormLabel>
                
                <CustomFileInput 
                  accept="image/*" 
                  onChange={handleFileChange} 
                  buttonText={t("custom.chooseFileButton")}
                  noFileText={t("custom.noFileChosen")}
                />
                
                {imagePreviewUrl && (
                  <div className="mt-2 flex flex-col items-center">
                    <div className="relative">
                      <img
                        src={imagePreviewUrl}
                        alt="Preview"
                        className="max-h-40 max-w-full rounded border border-gray-200 dark:border-gray-700 object-contain"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white dark:bg-gray-800"
                        onClick={() => { setImageFile(null); setImagePreviewUrl(null); }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("custom.name")}*</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-white dark:bg-gray-700 dark:text-white border border-gray-400" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="body_part"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("bodyPart")}*</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-white dark:bg-gray-700 dark:text-white border border-gray-400" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="target_muscle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("targetMuscle")}</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-white dark:bg-gray-700 dark:text-white border border-gray-400" />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="equipment"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("equipment")}</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-white dark:bg-gray-700 dark:text-white border border-gray-400" />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="difficulty"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("custom.difficulty")}</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-white dark:bg-gray-700 dark:text-white border border-gray-400" />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t("custom.instruction") || "Instruções"}
                </h3>
                
                {[1, 2, 3, 4, 5].map(n => (
                  <FormField
                    key={n}
                    control={form.control}
                    name={`instruction_${n}` as any}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          {t("custom.instruction")} {n}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} className="bg-white dark:bg-gray-700 dark:text-white border border-gray-400" />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
              
              <div className="pt-4 flex md:justify-end">
                <LoadingButton 
                  type="submit" 
                  loading={loading}
                  className="bg-flame hover:bg-flame/90 w-full md:w-auto"
                >
                  {t("custom.createButton")}
                </LoadingButton>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
